import React, { useEffect, useMemo, useState } from 'react';
import styles from './WorkplaceCalendarToolbar.module.scss';
import WorkplaceCalendarFilter, {
	WorkplaceCalendarWithFilterDefaultValues,
	WorkplaceCalendarWithFilterValues,
} from './WorkplaceCalendarFilter';
import CalendarPicker from '../CalendarPicker/CalendarPicker';
import { Moment } from 'moment';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import { formatObjectForQuery } from '../../../../Services/Api/FormatObjectForQuery';
import Loading from '../../../../Components/Loading';
import OverlayLoading from '../../../../Components/OverlayLoading';
import { joinClasses } from '../../../../Utils/className.util';
import LocalStorage from '../../../../Services/LocalStorage';
import { LocalStorageKey } from '../../../Base/Enums/LocalStorageKey.enum';
import CalendarSettingsModal from '../CalendarSettings/CalendarSettingsModal';
import { useAppContext } from '../../../../Services/AppContext';
import { CalendarViewIntervalEnum } from '../../Enums/CalendarViewInterval.enum';
import WorkplaceCalendarSlotDurationSwitch from './WorkplaceCalendarSlotDurationSwitch';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { CustomSettingKey } from '../../../Base/Enums/CustomSettingKey.enum';
import EventListWithSearchModal from '../../../Events/Components/EventListWithSearch/EventListWithSearchModal';

interface Props {
	setFilter: (values: WorkplaceCalendarWithFilterValues) => void;
	filterDefaultValues: WorkplaceCalendarWithFilterDefaultValues;
	onDateSelect: (date: Moment) => void;
	onShowOnlyMe: () => void;
	onShowAll: () => void;
	workplaceId: string;
	filter?: WorkplaceCalendarWithFilterValues;
	onToolbarToggle: () => void;
	onSlotDurationChange: (slotDuration: CalendarViewIntervalEnum) => void;
	slotDurationValue?: SelectValue;
	onCalendarSettingsChange: () => void;
	handleShowEventInCalendar: (eventId: string) => void;
}

const WorkplaceCalendarToolbar: React.FC<Props> = props => {
	const {
		setFilter,
		filterDefaultValues,
		onDateSelect,
		onShowOnlyMe,
		onShowAll,
		filter,
		onToolbarToggle,
		onSlotDurationChange,
		slotDurationValue,
		onCalendarSettingsChange,
		handleShowEventInCalendar,
	} = props;
	const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
	const workplaceId = filter?.workplaceId
		? filter.workplaceId
		: props.workplaceId;
	const { userData } = useAppContext();
	const { displayError } = useApiClient();
	const { showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [isCalendarSettingOpen, setIsCalendarSettingOpen] = useState(false);
	const [dateDateRange, setDateRange] = useState<
		| {
				dateFrom: Moment;
				dateTo: Moment;
		  }
		| undefined
	>(undefined);
	const [isPatientEventsSearchOpen, setIsPatientEventsSearchOpen] =
		useState(false);

	const [availabilities, setAvailabilities] = useState<
		{ date: string; percentageFree: number }[]
	>([]);

	const [isToolbarOpen, setIsToolbarOpen] = useState(
		(LocalStorage.get(LocalStorageKey.CALENDAR_TOOLBAR) as boolean) ?? true,
	);

	const handleToggleToolbar = () => {
		setIsToolbarOpen(prev => {
			LocalStorage.set(LocalStorageKey.CALENDAR_TOOLBAR, !prev);
			return !prev;
		});
		onToolbarToggle();
	};

	const handleOpenAbsenceCalendar = () => {
		window.location.href = '/kalendar/absence';
	};

	const handleDateRangeChange = (dateFrom: Moment, dateTo: Moment) => {
		setDateRange({ dateFrom, dateTo });
	};

	const getAvailability = async () => {
		if (!dateDateRange) return;
		setIsLoadingAvailability(true);
		try {
			const params = formatObjectForQuery({
				dateFrom: dateDateRange.dateFrom,
				dateTo: dateDateRange.dateTo,
				userIds: filter?.doctorIds,
			});
			const response = await apiClient.get(
				`/workplaces/${workplaceId}/calendar/availability/doctor-availability?${params}`,
			);
			if (response.status === 200) {
				setAvailabilities(response.data);
			} else {
				showNotificationAlert({
					content: t('base.shared.warningBug'),
				});
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoadingAvailability(false);
	};

	const handleOnSaveCalendarSettings = () => {
		setIsCalendarSettingOpen(false);
		onCalendarSettingsChange();
	};

	useEffect(() => {
		getAvailability();
	}, [dateDateRange, filter]);

	useEffect(() => {
		setIsFilterLoading(true);
		setTimeout(() => {
			setIsFilterLoading(false);
		}, 500);
	}, [filterDefaultValues]);

	const handleOnShowEventInCalendarClick = (eventId: string) => {
		setIsPatientEventsSearchOpen(false);
		handleShowEventInCalendar(eventId);
	};

	const isOnlyMe: boolean = useMemo(
		() =>
			filter?.doctorIds?.length === 1 && filter.doctorIds[0] === userData.id,
		[filter, userData.id],
	);

	return (
		<>
			<div
				className={joinClasses([
					styles.masterWrapper,
					!isToolbarOpen ? styles.noGap : '',
				])}>
				<div className={joinClasses([styles.toggleBar, styles.hiddenBar])}>
					<LinkButton
						onClick={handleToggleToolbar}
						title={
							isToolbarOpen
								? 'component.workplaceCalendarToolbar.hideMiniCalendarAndFilters'
								: 'component.workplaceCalendarToolbar.showMiniCalendarAndFilters'
						}
						theme={Theme.BLUE}
						className={joinClasses([
							styles.toggleButton,
							isToolbarOpen ? styles.close : styles.open,
						])}
					/>
				</div>
				<div
					className={joinClasses([
						styles.wrapper,
						!isToolbarOpen ? styles.hidden : '',
					])}>
					<div className={styles.calendarPicker}>
						<CalendarPicker
							onDateSelect={onDateSelect}
							availabilities={availabilities}
							onDateRangeChange={handleDateRangeChange}
						/>
						{isLoadingAvailability && <OverlayLoading />}
					</div>
					<div className={styles.filter}>
						{isFilterLoading ? (
							<Loading />
						) : (
							<WorkplaceCalendarFilter
								onFilter={setFilter}
								defaultValues={filterDefaultValues}
							/>
						)}
						<WorkplaceCalendarSlotDurationSwitch
							onSlotDurationChange={onSlotDurationChange}
							value={slotDurationValue}
						/>
					</div>
					<div className={styles.controls}>
						{isOnlyMe ? (
							<LinkButton
								title='component.workplaceCalendarToolbar.showAll'
								onClick={onShowAll}
								theme={Theme.BLUE}
							/>
						) : (
							<LinkButton
								title='component.workplaceCalendarToolbar.showOnlyMe'
								onClick={onShowOnlyMe}
								theme={Theme.BLUE}
							/>
						)}
						<LinkButton
							title='component.workplaceCalendarToolbar.absenceCalendar'
							onClick={handleOpenAbsenceCalendar}
							theme={Theme.BLUE}
						/>
						<LinkButton
							title='component.workplaceCalendarToolbar.calendarSettings'
							theme={Theme.BLUE}
							onClick={() => setIsCalendarSettingOpen(true)}
						/>
						<LinkButton
							title='component.workplaceCalendarToolbar.searchPatient'
							theme={Theme.BLUE}
							onClick={() => setIsPatientEventsSearchOpen(true)}
						/>
					</div>
				</div>
			</div>
			{isCalendarSettingOpen && (
				<CalendarSettingsModal
					isOpen
					userId={userData.id}
					onClose={() => setIsCalendarSettingOpen(false)}
					onSubmit={handleOnSaveCalendarSettings}
					customSettingKey={CustomSettingKey.CALENDAR_SETTINGS}
				/>
			)}
			{isPatientEventsSearchOpen && (
				<EventListWithSearchModal
					isOpen
					handleShowEventInCalendar={handleOnShowEventInCalendarClick}
					onClose={() => setIsPatientEventsSearchOpen(false)}
				/>
			)}
		</>
	);
};

export default WorkplaceCalendarToolbar;
